import Button from "@material-ui/core/Button"
import Switch from "@material-ui/core/Switch"
import Typography from "@material-ui/core/Typography"
import TextField from "@material-ui/core/TextField"
import Chip from "@material-ui/core/Chip"
import List from "@material-ui/core/List"
import FormControlLabel from "@material-ui/core/FormControlLabel"
import { FlexContainer } from "../../../lib/components/Container/Container"
import moment from "moment"
import { toNumber, reject } from "lodash"
import React, { useEffect, useState } from "react"
import { db } from "../../../firebase"
import CallKey from "./CallKey"
import CallListSidebar from "./CallListSidebar"
import useStyles from "./styles"
import CallListItem from "./CallListItem"
import Flex from "../../../lib/components/Flex/Flex"
import { TicketAssignee } from "../../../constants/enums"
import getHttpsCallable from "../../../utils/getHttpsCallable"


function TagFilters (
  { filters, setFilters }: {
    filters: string[],
    setFilters: (a: any) => void,
  },
) {
  return (
    <Flex wrap center>
      <Typography color='textPrimary' style={{ marginRight: 14 }}>
        Filters:
      </Typography>

      <Chip
        style={{ margin: 4 }}
        color={filters.includes("tlMissingNotRequested")
          ? "primary" : "default"}
        clickable
        label='TL missing (not requested)'
        onClick={() => {
          filters.includes("tlMissingNotRequested")
            ? setFilters(
              reject(filters, (t) => t === "tlMissingNotRequested"),
            )
            : setFilters(filters.concat("tlMissingNotRequested"))
        }}
      />
      <Chip
        style={{ margin: 4 }}
        color={filters.includes("tlMissingButRequested")
          ? "primary" : "default"}
        clickable
        label='TL missing (requested)'
        onClick={() => {
          filters.includes("tlMissingButRequested")
            ? setFilters(
              reject(filters, (t) => t === "tlMissingButRequested"),
            )
            : setFilters(filters.concat("tlMissingButRequested"))
        }}
      />
      <Chip
        style={{ margin: 4 }}
        color={filters.includes("fundingCommitted")
          ? "primary" : "default"}
        clickable
        label='Funding Committed'
        onClick={() => {
          filters.includes("fundingCommitted")
            ? setFilters(
              reject(filters, (t) => t === "fundingCommitted"),
            )
            : setFilters(filters.concat("fundingCommitted"))
        }}
      />
      <Chip
        style={{ margin: 4 }}
        color={filters.includes("superHighValue")
          ? "primary" : "default"}
        clickable
        label='Super High Value Claim'
        onClick={() => {
          filters.includes("superHighValue")
            ? setFilters(
              reject(filters, (t) => t === "superHighValue"),
            )
            : setFilters(filters.concat("superHighValue"))
        }}
      />
    </Flex>
  )
}

const getTemplates = getHttpsCallable("admin-getTemplates")

export default function BMWCallList () {
  const classes = useStyles()
  const [ticketRefs, setTicketRefs] = useState([])
  const [showNewClaims, setShowNewClaims] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [snapCount, setSnapCount] = useState(0)
  const [activeTicketRef, setActiveTicketRef] = useState(null)
  const [limit, setLimit] = useState(250)
  const [minReachedCount, setMinReachedCount] = useState<number>(0)
  const [filters, setFilters] = useState([])
  const [isFetchingTemplates, setIsFetchingTemplates] = useState(false)
  const [emailTemplates, setEmailTemplates] = useState([])

  useEffect(() => {
    if (!isFetchingTemplates) {
      setIsFetchingTemplates(true)
    } 
  }, [])

  useEffect(() => {
    if (!isFetchingTemplates) {
      return
    }

    async function fetchTemplates () {
      const dataEmailTemplate = await getTemplates({
        type: "email",
        label: "bmw",
      })
      setEmailTemplates(dataEmailTemplate?.data)
      setIsFetchingTemplates(false)
    }

    fetchTemplates()
  }, [isFetchingTemplates])

  useEffect(() => {
    let q = db.collection("tickets")
      .where("assignedGroup", "==", TicketAssignee.AWAITING_KV)
      .where("type", "==", "bmw-claim")
      .where("isLead", "==", false)
      .where("hasPhoneNumber", "==", true)
      .where("phoneNumberValid", "==", true)
      .where("reachedCount", ">=", toNumber(minReachedCount) || 0)
      .orderBy("reachedCount", "asc")
      .orderBy("callCount", "asc")
      .orderBy("createdAt", "desc")
      .limit(limit)

    if (filters.length) {
      q = q.where("tags", "array-contains-any", filters)
    }
    return q.onSnapshot(snap => {
      // @ts-expect-error
      setTicketRefs(snap.docs.filter(s => {
        // client filter to only older than 72 hours with callcount of 0
        const ticket = s.data()
        if (!ticket.createdAt) {
          return false
        }

        if (ticket.callCount >= 3) {
          return false
        }

        if (!showNewClaims) {
          if (
            ticket.callCount === 0 &&
            moment(ticket.createdAt.toDate())
              .isAfter(moment().subtract(72, "hours"))
          ) {
            return false
          }
        }
        return true
      }))
      setLoaded(true)
      setSnapCount(snap.size)
    })
  }, [limit, showNewClaims, minReachedCount, filters])

  if (loaded && ticketRefs.length === 0 && snapCount === limit) {
    setLimit(limit + 250)
  }

  const showMore = loaded && (snapCount >= limit)

  return (
    <>
      <Flex justify='between' wrap>
        <CallKey />

        <TextField
          label='Min Reached Count'
          type='number'
          InputLabelProps={{
            shrink: true,
          }}
          onChange={(event) =>
            setMinReachedCount(toNumber(event.target.value))
          }
        />
        <FormControlLabel
          control={<Switch onClick={() => setShowNewClaims(!showNewClaims)} />}
          label='Show new claims'
        />

      </Flex>
      <FlexContainer px={0}>
        <List className={classes.list}>
          {ticketRefs.map((ticketRef, i) => (
            <CallListItem
              key={i}
              ticketRef={ticketRef}
              emailTemplates={emailTemplates}
              onRowClicked={(ticketRef: any) => setActiveTicketRef(ticketRef)}
              // @ts-expect-error
              active={activeTicketRef && (ticketRef.id === activeTicketRef.id)}
            />
          ))}
          {showMore &&
            <Button fullWidth onClick={() => setLimit(limit + 250)}>
              Show More
            </Button>
          }
        </List>
        {activeTicketRef && (
          <CallListSidebar
            ticketRef={activeTicketRef}
            className={classes.sidebar}
          />
        )}
      </FlexContainer>
    </>
  )
}
